import * as React from 'react';

import { Button } from '@lightspeed/flame/Button';
import { Flex } from '@lightspeed/flame/Core';

import { ConfirmClose } from 'root/apps/InventoryCountScanQueue/views/ConfirmClose';
import { readScanEvent, SCAN_EVENT } from 'root/scan/ScanEvent';

import { ScanDataContext } from '../context/ScanData';
import {
  StyledItemDescription,
  StyledModal,
  StyledModalBody,
  StyledModalFooter,
  StyledModalHeader,
} from './ScanQueue.style';
import ScanQueueList from './ScanQueueList';

interface ScanQueueModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ScanQueueModal: React.FC<ScanQueueModalProps> = ({ isOpen, onClose }) => {
  const { failedCollection, pendingCollection, doneCollection, purgeByStatus, updateCollections } =
    React.useContext(ScanDataContext);
  const [isClearAllEnable, setIsClearAllEnable] = React.useState(false);
  const [isConfirmCloseOpened, setIsConfirmCloseOpened] = React.useState(false);
  const [canBeSafelyClosed, setCanBeSafelyClosed] = React.useState(true);

  React.useEffect(() => {
    if (pendingCollection.length || doneCollection.length || failedCollection.length) {
      setIsClearAllEnable(true);
    } else {
      setIsClearAllEnable(false);
    }
  }, [failedCollection, pendingCollection, doneCollection]);

  React.useEffect(() => {
    setCanBeSafelyClosed(!pendingCollection.length);
  }, [pendingCollection, setCanBeSafelyClosed]);

  React.useEffect(() => {
    const handleBarcodeScan = (event: Event) => {
      const barcodeEvent = readScanEvent(event);
      if (barcodeEvent) {
        setIsConfirmCloseOpened(false);
      }
    };

    window.removeEventListener(SCAN_EVENT, handleBarcodeScan);
    window.addEventListener(SCAN_EVENT, handleBarcodeScan);
    return () => {
      window.removeEventListener(SCAN_EVENT, handleBarcodeScan);
    };
  }, []);

  const handleClearAllSection = () => {
    purgeByStatus().then(() => {
      updateCollections();
    });
  };

  const handleOnRequestClose = () => {
    if (!canBeSafelyClosed) {
      setIsConfirmCloseOpened(true);
      return;
    }
    onClose();
  };

  const handleConfirmCloseConfirm = () => {
    setIsConfirmCloseOpened(false);
    setCanBeSafelyClosed(true);
    onClose();
  };

  const handleConfirmCloseCancel = () => {
    setIsConfirmCloseOpened(false);
  };

  React.useEffect(() => {
    if (!isOpen) {
      setIsConfirmCloseOpened(false);
      setCanBeSafelyClosed(true);
    }
  }, [isOpen]);

  return (
    <StyledModal
      isOpen={isOpen}
      onRequestClose={() => handleOnRequestClose()}
      aside
      data-test="scan-queue-modal"
    >
      <StyledModalHeader data-test="scan-queue-modal-header">
        <StyledItemDescription>Your scanned items</StyledItemDescription>
      </StyledModalHeader>
      <StyledModalBody data-test="scan-queue-modal-body">
        {isConfirmCloseOpened ? (
          <ConfirmClose
            canBeSafelyClosed={canBeSafelyClosed}
            onCancel={handleConfirmCloseCancel}
            onConfirm={handleConfirmCloseConfirm}
          />
        ) : null}
        <ScanQueueList />
      </StyledModalBody>
      <StyledModalFooter data-test="scan-queue-footer">
        <Flex justifyContent="flex-end">
          <Button
            disabled={!isClearAllEnable}
            onClick={handleClearAllSection}
            data-test="scan-queue-clear-all-section-btn"
          >
            Clear all sections
          </Button>
        </Flex>
      </StyledModalFooter>
    </StyledModal>
  );
};

export default ScanQueueModal;
